import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { AREAS, HOME_AREAS, CONDITIONS, IMG } from "@/content/site";
import { ARTICLES } from "@/content/approfondimenti";

const _dateFmt = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", year: "numeric" });
const formatDate = (d) => {
  try {
    return _dateFmt.format(new Date(d));
  } catch (e) {
    return d;
  }
};

function Parallax({ src, alt, className, gray }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className={`absolute inset-0 w-full h-[116%] object-cover ${gray ? "grayscale-[20%]" : ""}`} />
      <div className="absolute inset-0 ring-1 ring-inset ring-current/10" />
    </div>
  );
}

export function ConditionsGrid() {
  return (
    <section id="cosa-trattare" data-testid="conditions-grid" className="py-24 md:py-32 bg-graphite text-canvas">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal><Overline className="text-canvas/70">Cosa vuoi trattare?</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.06] tracking-tight">
            Spesso non conosci il nome della tecnologia. Conosci il tuo problema.
          </Reveal>
          <Reveal delay={0.18} as="p" className="mt-6 text-lg text-canvas/55 leading-relaxed">
            Parti dalla condizione che vuoi migliorare: ti guidiamo alla valutazione e al percorso più adatto.
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-canvas/15 border border-canvas/15">
          {CONDITIONS.map((c, i) => (
            <Reveal key={c.label} delay={(i % 3) * 0.04}>
              <Link to={c.to} data-testid={`condition-${i}`} className="group block h-full bg-graphite p-7 hover:bg-[#34383a] transition-colors duration-500">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl md:text-2xl">{c.label}</h3>
                  <ArrowRight className="w-4 h-4 text-sage opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
                <p className="mt-3 text-sm text-canvas/50 leading-relaxed">{c.blurb}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TecnologieTeaser() {
  const tech = AREAS["tecnologie"].cards;
  return (
    <section data-testid="home-tecnologie" className="py-24 md:py-32 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Reveal><Overline>Tecnologie disponibili</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Tecnologie differenti per bersagli differenti.
          </Reveal>
          <Reveal delay={0.18} as="p" className="mt-6 max-w-md text-lg text-muted leading-relaxed">
            Più piattaforme permettono di non adattare ogni problema a un unico dispositivo. Ogni
            tecnologia viene scelta in funzione del bersaglio, della profondità e della risposta biologica.
          </Reveal>
          <Reveal delay={0.24} as="div" className="mt-9">
            <Link to="/tecnologie" data-testid="home-tecnologie-cta" className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-ink">
              <span className="link-underline">Scopri le tecnologie</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          {tech.map((t, i) => (
            <Reveal key={t.title} delay={(i % 2) * 0.05} className="bg-canvas p-6 hover:bg-sand transition-colors duration-500">
              <span className="font-serif text-sage text-sm">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 font-serif text-xl leading-tight">{t.title}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HomeAreas() {
  return (
    <section id="aree" data-testid="home-areas" className="py-20 md:py-28 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal><Overline>I percorsi</Overline></Reveal>
        <div className="mt-14 space-y-24 md:space-y-32">
          {HOME_AREAS.map((key, idx) => {
            const a = AREAS[key];
            const flip = idx % 2 === 1;
            return (
              <div key={key} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                  <Parallax src={a.image} alt={a.label} className="h-[44vh] lg:h-[60vh] w-full" gray={a.dark} />
                </div>
                <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                  <Reveal><span className="font-serif text-6xl md:text-7xl text-ink/12">{String(idx + 1).padStart(2, "0")}</span></Reveal>
                  <Reveal delay={0.08}><Overline>{a.overline}</Overline></Reveal>
                  <Reveal delay={0.14} as="h3" className="mt-5 font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">{a.hero}</Reveal>
                  <Reveal delay={0.2} as="p" className="mt-6 max-w-lg text-lg text-muted leading-relaxed">{a.intro[0]}</Reveal>
                  <Reveal delay={0.26} as="div" className="mt-8">
                    <Link to={`/${key}`} data-testid={`home-area-${key}`} className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-ink">
                      <span className="link-underline">Scopri {a.label}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function DoctorTeaser() {
  const navigate = useNavigate();
  return (
    <section data-testid="home-doctor" className="py-24 md:py-36 bg-sand">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Parallax src={IMG.doctor} alt="Dott. Alessandro Valle" className="h-[54vh] lg:h-[68vh] w-full" gray />
        </div>
        <div className="lg:col-span-7">
          <Reveal><Overline>Il medico</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">Dott. Alessandro <span className="italic text-sagedark">Valle</span></Reveal>
          <Reveal delay={0.16} as="p" className="mt-6 max-w-xl text-lg text-muted leading-relaxed">
            Medico focalizzato su trattamenti laser, Skin Quality e Tricologia, con formazione clinica
            in ambito dermatologico e immunologico (San Raffaele, Niguarda) e un approccio prevalentemente
            non volumetrico orientato alla qualità dei tessuti.
          </Reveal>
          <Reveal delay={0.22} as="p" className="mt-6 font-serif italic text-xl text-graphite max-w-lg">
            La tecnologia è efficace solo quando si comprende quale tessuto trattare e quale risposta biologica ottenere.
          </Reveal>
          <Reveal delay={0.28} as="div" className="mt-9">
            <button onClick={() => navigate("/dott-alessandro-valle")} data-testid="home-doctor-cta" className="group relative inline-flex items-center overflow-hidden border border-ink px-9 py-4 text-xs uppercase tracking-[0.2em]">
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-canvas transition-colors duration-500">Scopri il profilo</span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


export function StartHereCta() {
  return (
    <section data-testid="home-start-here" className="py-16 md:py-20 bg-graphite text-canvas">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] text-sage mb-4">Da dove iniziare?</p>
            <h2 className="font-serif font-light text-2xl md:text-3xl lg:text-4xl leading-tight">
              Non sai quale trattamento scegliere?
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-canvas/65">
              Non partire dalla tecnologia. Parti dal problema. Un breve percorso orientativo per
              capire quale area approfondire — non sostituisce la valutazione medica.
            </p>
          </div>
          <Link to="/da-dove-iniziare" data-testid="home-start-here-cta" className="group relative inline-flex items-center overflow-hidden border border-canvas/40 px-8 py-4 text-xs uppercase tracking-[0.2em] shrink-0">
            <span className="absolute inset-0 bg-canvas translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative group-hover:text-graphite transition-colors duration-500 inline-flex items-center gap-2">Inizia il percorso <ArrowRight className="w-4 h-4" /></span>
          </Link>
        </div>
      </div>
    </section>
  );
}


export function ApprofondimentiTeaser() {
  const posts = ARTICLES.slice(0, 3);
  if (posts.length === 0) return null;
  return (
    <section data-testid="home-approfondimenti" className="py-24 md:py-36 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal><Overline>Approfondimenti</Overline></Reveal>
            <Reveal delay={0.1} as="h2" className="mt-5 font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight max-w-2xl">
              Capire prima di trattare
            </Reveal>
          </div>
          <Reveal delay={0.16} as="div">
            <Link to="/approfondimenti" data-testid="home-approfondimenti-all" className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-ink">
              <span className="link-underline">Tutti gli articoli</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {posts.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.06}>
              <Link to={`/approfondimenti/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group flex flex-col h-full bg-canvas hover:bg-sand transition-colors duration-500">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.cover} alt={a.coverAlt || a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
                <div className="flex flex-col flex-1 p-7">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-sagedark">{formatDate(a.datePublished)}</span>
                  <h3 className="mt-3 font-serif text-xl md:text-2xl leading-tight">{a.title}</h3>
                  <span className="mt-auto pt-5 inline-flex items-center gap-2 text-sm text-ink group-hover:gap-3 transition-all duration-300">
                    Leggi l'approfondimento <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
